/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, ErrorInfo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDownIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import SearchBar from "../ui/SearchBar";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { Modal } from "../ui/modal";
import { PageLoadingView } from "../common/PageLoadingView";
import { PageErrorView } from "../common/PageErrorView";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { useUser } from "../../context/UserInfoProvider";
import { useAnalytics } from "../../hooks/useAnalytics";
import { Employee as BaseEmployee, Role } from "../../api/employeeApi";

// Helper to convert technical API errors into user-friendly messages
const getFriendlyErrorMessage = (error: any): string => {
  const rawMsg =
    (error as any)?.response?.data?.error?.message ||
    (error as any)?.message ||
    String(error);

  // Detect Postgres "value too long" errors (e.g. "value too long for type character varying(20)")
  if (
    rawMsg.toLowerCase().includes("value too long for type character varying")
  ) {
    const match = rawMsg.match(/character varying\((\d+)\)/i);
    const limit = match ? match[1] : "the allowed";
    return `Title is too long. Please keep it under ${limit} characters.`;
  }

  // Detect validation errors (invalid data format)
  if (
    rawMsg.toLowerCase().includes("invalid") ||
    rawMsg.toLowerCase().includes("validation")
  ) {
    return "Please check your input values for invalid formats.";
  }

  return rawMsg;
};

// Helper to validate name input (only allow alphabet, apostrophe, and hyphen)
const isValidNameChar = (char: string): boolean => {
  return /^[a-zA-Z\s'-]$/.test(char);
};

// Helper to validate email format
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Helper to handle name input changes with validation
const handleNameInputChange = (
  value: string,
  setter: (value: string) => void,
) => {
  const sanitized = value
    .split("")
    .filter((char) => isValidNameChar(char))
    .join("");
  setter(sanitized);
};

// Extend the base Employee type with platformUserId so we can surface it in the UI
interface Employee extends BaseEmployee {
  platformUserId?: string;
  email?: string; // primary email
}

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error in EmployeeManagement:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-red-500 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface EditEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee | null;
  roles: Role[];
  onSave: (employee: Employee) => void;
}

const EditEmployeeModal: React.FC<EditEmployeeModalProps> = ({
  isOpen,
  onClose,
  employee,
  roles,
  onSave,
}) => {
  const [firstName, setFirstName] = useState(
    employee?.name?.split(" ")[0] || "",
  );
  const [lastName, setLastName] = useState(employee?.name?.split(" ")[1] || "");
  const [title, setTitle] = useState(employee?.title || "");
  const [selectedRoleId, setSelectedRoleId] = useState(employee?.roleId || "");
  const [email, setEmail] = useState(employee?.email || "");
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSave = () => {
    setError(null);

    // Validate all required fields
    const errors: string[] = [];

    if (!firstName?.trim()) {
      errors.push("First name is required");
    }
    if (!lastName?.trim()) {
      errors.push("Last name is required");
    }
    if (!selectedRoleId?.trim()) {
      errors.push("Role is required");
    }
    if (!email?.trim()) {
      errors.push("Email is required");
    } else if (!isValidEmail(email.trim())) {
      errors.push("Please enter a valid email address");
    }
    if (title.trim().length > 20) {
      errors.push("Title must be 20 characters or less");
    }

    if (errors.length > 0) {
      setError(errors.join(", "));
      return;
    }

    // Find the selected role
    const selectedRoleData = roles?.find((r) => r.roleId === selectedRoleId);
    if (!selectedRoleData) {
      setError("Please select a valid role");
      return;
    }

    // Check if any values have actually changed
    const fullName = `${firstName} ${lastName}`.trim();
    const hasNameChanged = fullName !== employee?.name;
    const hasTitleChanged = title.trim() !== employee?.title;
    const hasRoleChanged = selectedRoleId !== employee?.roleId;
    const hasEmailChanged = (email || "").trim() !== (employee?.email || "");

    // If nothing has changed, just close the modal
    if (
      !hasNameChanged &&
      !hasTitleChanged &&
      !hasRoleChanged &&
      !hasEmailChanged
    ) {
      onClose();
      return;
    }

    onSave({
      id: employee?.id || "",
      name: fullName,
      title: title.trim(),
      roleId: selectedRoleId,
      email: email.trim(),
      role: selectedRoleData.name,
      roleColor:
        selectedRoleData.name.toLowerCase() === "manager"
          ? "bg-blue-500"
          : selectedRoleData.name.toLowerCase() === "staff"
            ? "bg-green-500"
            : selectedRoleData.name.toLowerCase() === "intern"
              ? "bg-yellow-500"
              : "bg-gray-500",
      createdAt: employee?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  // Don't render if roles aren't loaded yet
  if (!roles || roles.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96">
          <div className="text-center">
            <p className="text-red-500">Loading roles...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
          {employee?.id ? "Edit Employee" : "Add New Employee"}
        </h2>
        {error && (
          <div className="mb-4 p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
            {error}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) =>
                handleNameInputChange(e.target.value, setFirstName)
              }
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              placeholder="Enter first name (letters, ', - only)"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) =>
                handleNameInputChange(e.target.value, setLastName)
              }
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              placeholder="Enter last name (letters, ', - only)"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Title <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={20}
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              placeholder="Enter title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              placeholder="Enter email"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              required
            >
              <option value="">Select a role</option>
              {roles?.map((role) => (
                <option key={role.roleId} value={role.roleId}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600"
          >
            {employee?.id ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface AddEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  roles: Role[];
  onSave: (employee: {
    roleId: string;
    email: string;
    name: {
      firstName: string;
      lastName: string;
      title: string;
    };
  }) => void;
  isCreating: boolean;
  error?: string | null;
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({
  isOpen,
  onClose,
  roles,
  onSave,
  isCreating,
  error: externalError,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSave = () => {
    setError(null);

    // Validate all required fields
    const errors: string[] = [];

    if (!firstName?.trim()) {
      errors.push("First name is required");
    }
    if (!lastName?.trim()) {
      errors.push("Last name is required");
    }
    if (!selectedRoleId?.trim()) {
      errors.push("Role is required");
    }
    if (!email?.trim()) {
      errors.push("Email is required");
    } else if (!isValidEmail(email.trim())) {
      errors.push("Please enter a valid email address");
    }
    if (title.trim().length > 20) {
      errors.push("Title must be 20 characters or less");
    }

    if (errors.length > 0) {
      setError(errors.join(", "));
      return;
    }

    // Find the selected role to get its name
    const selectedRole = roles?.find((r) => r.roleId === selectedRoleId);
    if (!selectedRole) {
      setError("Please select a valid role");
      return;
    }

    onSave({
      roleId: selectedRoleId,
      email: email.trim(),
      name: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        title: title.trim(),
      },
    });
  };

  // Don't render if roles aren't loaded yet
  if (!roles || roles.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96">
          <div className="text-center">
            <p className="text-red-500">Loading roles...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
          Add New Employee
        </h2>
        {(error || externalError) && (
          <div className="mb-4 p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
            {externalError || error}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              placeholder="Enter email"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) =>
                handleNameInputChange(e.target.value, setFirstName)
              }
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              placeholder="Enter first name (letters, ', - only)"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) =>
                handleNameInputChange(e.target.value, setLastName)
              }
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              placeholder="Enter last name (letters, ', - only)"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Title <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={20}
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              placeholder="Enter title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              required
            >
              <option value="">Select a role</option>
              {roles?.map((role) => (
                <option key={role.roleId} value={role.roleId}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600"
            disabled={isCreating}
          >
            {isCreating ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner size="sm" color="primary" />
                <span>Adding...</span>
              </div>
            ) : (
              "Add"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================
// Bulk Add Employees Modal
// =============================

// Input shape for each row
interface BulkEmployeeInput {
  roleId: string;
  email: string;
  name: {
    firstName: string;
    lastName: string;
    title: string;
  };
}

interface BulkAddEmployeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  roles: Role[];
  onSave: (employees: BulkEmployeeInput[]) => Promise<
    {
      email: string;
      success: boolean;
      errorMessage?: string;
    }[]
  >;
  isCreating: boolean;
  duplicateEmails?: string[];
  error?: string | null; // External error from parent
  retainedRows?: BulkEmployeeInput[] | null; // Rows to hydrate after reload
}

const BulkAddEmployeesModal: React.FC<BulkAddEmployeesModalProps> = ({
  isOpen,
  onClose,
  roles,
  onSave,
  isCreating,
  duplicateEmails = [],
  error: externalError,
  retainedRows,
}) => {
  const [rows, setRows] = useState<BulkEmployeeInput[]>([
    {
      roleId: "",
      email: "",
      name: { firstName: "", lastName: "", title: "" },
    },
  ]);

  const [defaultRoleId, setDefaultRoleId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<{ [key: number]: string[] }>({});
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  type ImportPreview = {
    duplicates: BulkEmployeeInput[];
    parsed: BulkEmployeeInput[];
  } | null;
  const [importPreview, setImportPreview] = useState<ImportPreview>(null);
  const [showCSVModal, setShowCSVModal] = useState(false);

  useEffect(() => {
    if (defaultRoleId) {
      setRows((prev) =>
        prev.map((r) => ({ ...r, roleId: r.roleId || defaultRoleId })),
      );
    }
  }, [defaultRoleId]);

  // Hydrate rows with retained rows from parent (e.g., after reload)
  useEffect(() => {
    if (retainedRows && retainedRows.length > 0) {
      setRows(retainedRows);
    }
  }, [retainedRows]);

  if (!isOpen) return null;

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        roleId: defaultRoleId || "",
        email: "",
        name: { firstName: "", lastName: "", title: "" },
      },
    ]);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx: number, updated: Partial<BulkEmployeeInput>) => {
    setRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...updated } : row)),
    );
  };

  const handleSave = async () => {
    setError(null);
    setRowErrors({});

    // Validate all required fields and length restrictions
    const errors: string[] = [];
    const newRowErrors: { [key: number]: string[] } = {};

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNum = i + 1;
      const rowErrorsList: string[] = [];

      if (!row.email.trim()) {
        const msg = "Email is required";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      } else if (!isValidEmail(row.email.trim())) {
        const msg = "Invalid email format";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }
      if (!row.name.firstName.trim()) {
        const msg = "First name is required";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }
      if (!row.name.lastName.trim()) {
        const msg = "Last name is required";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }
      if (!row.roleId.trim()) {
        const msg = "Role is required";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }
      if (row.name.title.trim().length > 20) {
        const msg = "Title must be 20 characters or less";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }
      if (row.name.firstName.trim().length > 50) {
        const msg = "First name must be 50 characters or less";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }
      if (row.name.lastName.trim().length > 50) {
        const msg = "Last name must be 50 characters or less";
        rowErrorsList.push(msg);
        errors.push(`Row ${rowNum}: ${msg}`);
      }

      if (rowErrorsList.length > 0) {
        newRowErrors[i] = rowErrorsList;
      }
    }

    if (errors.length > 0) {
      setRowErrors(newRowErrors);
      setError(`Please fix the following errors:\n${errors.join("\n")}`);
      return;
    }

    try {
      const results = await onSave(rows);
      if (Array.isArray(results) && results.length > 0) {
        const failedEmails = results
          .filter((r) => !r.success)
          .map((r) => r.email.toLowerCase());
        // Keep only failed rows so user can correct them
        if (failedEmails.length > 0) {
          setRows((prev) =>
            prev.filter((r) => failedEmails.includes(r.email.toLowerCase())),
          );
        }
      }
    } catch {
      // Let parent error handling surface in externalError
    }
  };

  // CSV import handler
  const handleImportCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      if (!text) return;

      try {
        const lines = text.split(/\r?\n/).filter(Boolean);
        if (lines.length === 0) throw new Error("CSV file is empty");

        // Determine headers
        const headers = lines[0]
          .split(/,|;|\t/)
          .map((h) => h.trim().toLowerCase());
        const idx = (name: string) =>
          headers.findIndex((h) => h === name.toLowerCase());
        const firstIdx = idx("firstname");
        const lastIdx = idx("lastname");
        const titleIdx = idx("title");
        const emailIdx = idx("email");
        const roleIdx = idx("role");

        if (
          firstIdx === -1 ||
          lastIdx === -1 ||
          emailIdx === -1 ||
          roleIdx === -1
        ) {
          throw new Error(
            "CSV must include headers: firstname, lastname, email, role (title optional)",
          );
        }

        const newRows: BulkEmployeeInput[] = lines.slice(1).map((line) => {
          // Split the row into cells, trim whitespace, and **limit the array to the
          // header length** so any extra columns are ignored. This prevents cases
          // where the role cell is shifted into the title position if the CSV
          // lacks a dedicated Title column.
          const cols = line
            .split(/,|;|\t/)
            .map((c) => c.trim())
            .slice(0, headers.length);

          const roleName = cols[roleIdx] || "";
          const foundRole = roles.find(
            (r) => r.name.toLowerCase() === roleName.toLowerCase(),
          );

          return {
            roleId: foundRole ? foundRole.roleId : "",
            email: cols[emailIdx] || "",
            name: {
              firstName: cols[firstIdx] || "",
              lastName: cols[lastIdx] || "",
              title: titleIdx !== -1 ? cols[titleIdx] || "" : "",
            },
          } as BulkEmployeeInput;
        });

        if (newRows.length === 0) throw new Error("No data rows found in CSV");

        // Detect duplicates based on email against existing rows
        const existingEmails = new Set(rows.map((r) => r.email.toLowerCase()));
        const duplicates = newRows.filter((r) =>
          existingEmails.has(r.email.toLowerCase()),
        );

        if (duplicates.length > 0) {
          // Show preview modal
          setImportPreview({ duplicates, parsed: newRows });
          return; // wait for user decision
        }

        // No duplicates – merge immediately
        setRows((prev) => {
          const onlyBlank =
            prev.length === 1 && !prev[0].email && !prev[0].name.firstName;
          return onlyBlank ? newRows : [...prev, ...newRows];
        });
        const missingRoles = newRows.filter((r) => !r.roleId).length;
        if (missingRoles > 0) {
          setError(
            `${missingRoles} of ${newRows.length} record${
              missingRoles === 1 ? " has" : "s have"
            } roles that were not recognized. Please manually select role(s).`,
          );
        } else {
          setError(null);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to parse CSV";
        setError(msg);
      } finally {
        // reset input so same file can be selected again
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsText(file);
  };

  // Helper to actually add parsed rows
  const commitImportedRows = (rowsToAdd: BulkEmployeeInput[]) => {
    setRows((prev) => {
      const onlyBlank =
        prev.length === 1 && !prev[0].email && !prev[0].name.firstName;
      return onlyBlank ? rowsToAdd : [...prev, ...rowsToAdd];
    });
    const missingRoles = rowsToAdd.filter((r) => !r.roleId).length;
    if (missingRoles > 0) {
      setError(
        `${missingRoles} of ${rowsToAdd.length} record${
          missingRoles === 1 ? " has" : "s have"
        } roles that were not recognized. Please select a role before saving.`,
      );
    } else {
      setError(null);
    }
    setImportPreview(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000] overflow-auto p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
            Bulk Add Employees
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {(error || externalError) && (
          <div className="mb-4 p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded whitespace-pre-line">
            {externalError || error}
          </div>
        )}

        {/* Default role selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
            Default Role (applied to new rows)
          </label>
          <select
            value={defaultRoleId}
            onChange={(e) => setDefaultRoleId(e.target.value)}
            className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600"
          >
            <option value="">Select a role</option>
            {roles.map((role) => (
              <option key={role.roleId} value={role.roleId}>
                {role.name}
              </option>
            ))}
          </select>
        </div>

        {/* Rows table */}
        <div className="overflow-x-auto max-h-[50vh]">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead className="sticky top-0">
              <tr>
                <th className="px-2 py-2 w-12"></th>
                <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                  First Name*
                </th>
                <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                  Last Name*
                </th>
                <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                  Title
                </th>
                <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                  Email*
                </th>
                <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">
                  Role*
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isDup = duplicateEmails.includes(row.email.toLowerCase());
                const hasRowErrors =
                  rowErrors[idx] && rowErrors[idx].length > 0;
                return (
                  <React.Fragment key={idx}>
                    <tr
                      className={`border-b border-gray-200 dark:border-gray-700 ${
                        hasRowErrors ? "bg-red-50 dark:bg-red-900/10" : ""
                      }`}
                    >
                      {/* Row Number */}
                      <td className="px-2 py-2 text-center text-gray-600 dark:text-gray-400 w-12">
                        {idx + 1}
                      </td>
                      {/* First Name */}
                      <td className="px-3 py-2 align-top">
                        <input
                          type="text"
                          value={row.name.firstName}
                          onChange={(e) => {
                            const sanitized = e.target.value
                              .split("")
                              .filter((char) => isValidNameChar(char))
                              .join("");
                            updateRow(idx, {
                              name: { ...row.name, firstName: sanitized },
                            });
                          }}
                          maxLength={50}
                          className={`w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 ${
                            hasRowErrors ? "border-red-500" : ""
                          }`}
                          placeholder="First name (letters, ', - only)"
                        />
                      </td>
                      {/* Last Name */}
                      <td className="px-3 py-2 align-top">
                        <input
                          type="text"
                          value={row.name.lastName}
                          onChange={(e) => {
                            const sanitized = e.target.value
                              .split("")
                              .filter((char) => isValidNameChar(char))
                              .join("");
                            updateRow(idx, {
                              name: { ...row.name, lastName: sanitized },
                            });
                          }}
                          maxLength={50}
                          className={`w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 ${
                            hasRowErrors ? "border-red-500" : ""
                          }`}
                          placeholder="Last name (letters, ', - only)"
                        />
                      </td>
                      {/* Title */}
                      <td className="px-3 py-2 align-top">
                        <input
                          type="text"
                          value={row.name.title}
                          onChange={(e) =>
                            updateRow(idx, {
                              name: { ...row.name, title: e.target.value },
                            })
                          }
                          maxLength={20}
                          className="w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600"
                          placeholder="Title"
                        />
                      </td>
                      {/* Email */}
                      <td className="px-3 py-2 align-top">
                        <input
                          type="email"
                          value={row.email}
                          onChange={(e) =>
                            updateRow(idx, { email: e.target.value })
                          }
                          className={`w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 ${
                            hasRowErrors ? "border-red-500" : ""
                          } ${isDup ? "border-red-500" : ""}`}
                          placeholder="Email"
                        />
                      </td>
                      {/* Role */}
                      <td className="px-3 py-2 align-top">
                        <select
                          value={row.roleId}
                          onChange={(e) =>
                            updateRow(idx, { roleId: e.target.value })
                          }
                          className={`w-full p-1 border rounded bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 min-w-[120px] ${
                            hasRowErrors ? "border-red-500" : ""
                          }`}
                        >
                          <option value=""></option>
                          {roles.map((role) => (
                            <option
                              key={role.roleId}
                              value={role.roleId}
                              className="truncate"
                            >
                              {role.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      {/* Remove */}
                      <td className="px-1 py-2 text-center">
                        {rows.length > 1 && (
                          <button
                            onClick={() => removeRow(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {(hasRowErrors || isDup) && (
                      <tr>
                        <td colSpan={7} className="px-3 pt-1 pb-2">
                          <div className="text-xs text-red-600 whitespace-pre-line">
                            {[
                              ...(rowErrors[idx] || []),
                              ...(isDup ? ["Duplicate email"] : []),
                            ]
                              .filter(Boolean)
                              .join("\n")}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Bottom actions */}
        <div className="mt-4 flex flex-wrap justify-between items-center gap-3">
          {/* Left side actions */}
          <div className="flex gap-2">
            <button
              onClick={addRow}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
            >
              + Add Row
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
              >
                Import CSV
              </button>
              <button
                onClick={() => setShowCSVModal(true)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
              >
                Learn More
              </button>
            </div>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleImportCsv}
              className="hidden"
            />
          </div>

          {/* Right side actions */}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={isCreating}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isCreating}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600"
            >
              {isCreating ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" color="primary" />
                  <span>Adding...</span>
                </div>
              ) : (
                "Add Employees"
              )}
            </button>
          </div>
        </div>

        {/* Duplicate warning modal */}
        {importPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100001] p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full">
              <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">
                Duplicate Employees Detected
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                {importPreview.duplicates.length} of{" "}
                {importPreview.parsed.length} records have emails that already
                exist in the table.
              </p>
              <div className="flex flex-wrap justify-between items-center gap-4">
                {/* Left side */}
                <button
                  onClick={() =>
                    commitImportedRows(
                      importPreview.parsed.filter(
                        (r) => !importPreview.duplicates.includes(r),
                      ),
                    )
                  }
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
                >
                  Add Non-Duplicates
                </button>

                {/* Right side */}
                <div className="flex gap-2 ml-auto">
                  <button
                    onClick={() => commitImportedRows(importPreview.parsed)}
                    className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600 whitespace-nowrap"
                  >
                    Add All
                  </button>
                  <button
                    onClick={() => setImportPreview(null)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Employee CSV Format Modal */}
        <EmployeeCSVFormatModal
          isOpen={showCSVModal}
          onClose={() => setShowCSVModal(false)}
        />
      </div>
    </div>
  );
};

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  employeeName: string;
  isDeleting: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  employeeName,
  isDeleting,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
          Delete Employee
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Are you sure you want to delete {employeeName}? This action cannot be
          undone.
        </p>
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded hover:bg-red-500 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner size="sm" color="primary" />
                <span>Deleting...</span>
              </div>
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface RoleChangeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  employeeName: string;
  currentRole: string;
  newRole: string;
  isUpdating: boolean;
}

const RoleChangeConfirmationModal: React.FC<
  RoleChangeConfirmationModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  employeeName,
  currentRole,
  newRole,
  isUpdating,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
          Change Employee Role
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Are you sure you want to change {employeeName}'s role?
        </p>
        <div className="mb-6 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Current Role:
            </span>
            <span className="text-sm font-medium text-gray-800 dark:text-white">
              {currentRole}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              New Role:
            </span>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              {newRole}
            </span>
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            disabled={isUpdating}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isUpdating}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner size="sm" color="primary" />
                <span>Updating...</span>
              </div>
            ) : (
              "Confirm"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Employee CSV Format Modal Component
const EmployeeCSVFormatModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const csvExample = `firstname,lastname,email,role,title
John,Smith,john.smith@hotel.com,STAFF,Front Desk Agent
Sarah,Johnson,sarah.j@hotel.com,MANAGER,Operations Manager
Michael,Brown,m.brown@hotel.com,ADMIN,General Manager`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000] p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-2xl w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            What is the correct CSV format for employees?
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Your CSV file should include the following headers (title is
            optional):
          </p>
          <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
            <code className="text-sm text-gray-800 dark:text-gray-200">
              firstname, lastname, email, role, title
            </code>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Example CSV Format:
          </label>
          <textarea
            value={csvExample}
            readOnly
            className="w-full h-32 p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 font-mono text-sm resize-none"
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-500 dark:hover:bg-blue-600"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

interface SelfManageInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoToSettings: () => void;
}

const SelfManageInfoModal: React.FC<SelfManageInfoModalProps> = ({
  isOpen,
  onClose,
  onGoToSettings,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-lg p-6"
      showCloseButton={false}
    >
      <h2 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">
        Manage your profile in Settings
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
        For security, you can’t edit your own role or delete your own account
        here. Update your personal details from Settings instead.
      </p>
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
        >
          Close
        </button>
        <button
          onClick={() => {
            onClose();
            onGoToSettings();
          }}
          className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-500 dark:hover:bg-blue-600"
        >
          Go to Settings
        </button>
      </div>
    </Modal>
  );
};

const EmployeeManagement: React.FC = () => {
  const { callApi } = useAuthenticatedApi();
  const { platformUserId, employeeId: currentUserEmployeeId } = useUser();
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const [selfManageModalOpen, setSelfManageModalOpen] = useState(false);

  // Local state management (replacing useEmployees hook)
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [searchQuery, setSearchQuery] = useState("");
  // Local input for controlled search bar
  const [searchInput, setSearchInput] = useState<string>("");

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState<{
    [key: string]: boolean;
  }>({});
  const [updatingEmployeeId, setUpdatingEmployeeId] = useState<string | null>(
    null,
  );
  const [isDeletingEmployeeId, setIsDeletingEmployeeId] = useState<
    string | null
  >(null);
  const [isCreatingEmployee, setIsCreatingEmployee] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(
    null,
  );
  const [roleChangeModalOpen, setRoleChangeModalOpen] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    employeeId: string;
    employeeName: string;
    currentRole: string;
    newRole: Role;
  } | null>(null);
  const [isBulkAddingEmployees, setIsBulkAddingEmployees] = useState(false);
  const [duplicateEmails, setDuplicateEmails] = useState<string[]>([]);
  const [addEmployeeError, setAddEmployeeError] = useState<string | null>(null);
  const [bulkAddError, setBulkAddError] = useState<string | null>(null);
  const [bulkAddRetainedRows, setBulkAddRetainedRows] = useState<
    BulkEmployeeInput[] | null
  >(null);
  const [idsExpanded, setIdsExpanded] = useState<{ [key: string]: boolean }>(
    {},
  );

  // Helper function to get role color (moved outside to prevent re-creation)
  const getRoleColor = useCallback((roleName: string): string => {
    switch (roleName.toLowerCase()) {
      case "manager":
        return "bg-blue-500";
      case "staff":
        return "bg-green-500";
      case "intern":
        return "bg-yellow-500";
      default:
        return "bg-gray-500";
    }
  }, []);

  // Fetch employees using authenticated API
  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await callApi({
        method: "GET",
        url: `/management/employees?page=${pagination.page}&limit=${
          pagination.limit
        }$${
          searchQuery.trim()
            ? `&search=${encodeURIComponent(searchQuery.trim())}`
            : ""
        }`,
      });

      // Debug log to see structure

      // Check if response has expected structure
      if (!response.data || !Array.isArray(response.data.data)) {
        throw new Error("Invalid API response structure");
      }

      // Transform API response to match expected structure
      const transformedEmployees: Employee[] = response.data.data.map(
        (emp: {
          employeeId: string;
          platformUserId?: string;
          name: {
            fullName: string;
            title?: string;
            firstName?: string;
            lastName?: string;
            firstname?: string;
            lastname?: string;
          };
          contact?: { email?: string };
          role: { name: string; roleId: string };
          createdAt: string;
          updatedAt: string;
        }) => ({
          id: emp.employeeId,
          platformUserId: emp.platformUserId,
          name:
            [
              emp.name.firstName || emp.name.firstname || "",
              emp.name.lastName || emp.name.lastname || "",
            ]
              .join(" ")
              .trim() || emp.name.fullName,
          title: emp.name.title || "",
          role: emp.role.name,
          roleId: emp.role.roleId,
          roleColor: getRoleColor(emp.role.name),
          createdAt: emp.createdAt,
          updatedAt: emp.updatedAt,
          email: emp.contact?.email,
        }),
      );

      setEmployees(transformedEmployees);
      setPagination((prev) => ({
        ...prev,
        total:
          response.data.pagination?.total ||
          response.data.meta?.totalCount ||
          0,
        totalPages:
          response.data.pagination?.totalPages ||
          response.data.meta?.totalPages ||
          1,
      }));
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch employees",
      );
    } finally {
      setIsLoading(false);
    }
  }, [callApi, pagination.page, pagination.limit, getRoleColor, searchQuery]);

  // Fetch roles using authenticated API
  const fetchRoles = useCallback(async () => {
    setIsLoadingRoles(true);
    try {
      const response = await callApi({
        method: "GET",
        url: "/management/employees/roles/all",
      });
      // Define the expected shape coming back from the API (snake/camel casing differences)
      interface ApiRole {
        roleid?: string;
        roleId?: string;
        name: string;
        description?: string;
      }

      const apiRoles: ApiRole[] = Array.isArray(response.data?.data)
        ? (response.data.data as ApiRole[])
        : [];

      // Transform API response keys to match the Role interface expected by the UI
      const transformedRoles: Role[] = apiRoles.map((r) => ({
        roleId: r.roleId ?? r.roleid ?? "", // Handle different possible casings
        name: r.name,
        description: r.description ?? "",
      }));

      setRoles(transformedRoles);
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch roles");
    } finally {
      setIsLoadingRoles(false);
    }
  }, [callApi]);

  // Initialize roles on component mount
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Fetch employees when pagination changes
  useEffect(() => {
    fetchEmployees();
  }, [pagination.page, pagination.limit, fetchEmployees]);

  const handlePageChange = (newPage: number) => {
    if (
      pagination.total > 0 && // Add this check to prevent issues when total is 0
      (newPage < 1 || newPage > Math.ceil(pagination.total / pagination.limit))
    ) {
      return;
    }
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    if (![10, 20, 50].includes(newLimit)) {
      return;
    }
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleAddEmployee = () => {
    try {
      if (!roles || roles.length === 0) {
        throw new Error("No roles available. Please try again later.");
      }
      setAddEmployeeError(null); // Clear any previous modal errors
      setIsAddingEmployee(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add employee");
    }
  };

  const handleEditEmployee = (employee: Employee) => {
    try {
      if (!employee?.id) {
        throw new Error("Invalid employee data");
      }
      setEditingEmployee(employee);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to edit employee");
    }
  };

  const handleSaveEmployee = async (updatedEmployee: Employee) => {
    try {
      if (!updatedEmployee?.roleId) {
        throw new Error("No role selected");
      }

      // Safely split the name
      const nameParts = (updatedEmployee.name || "").trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      if (!firstName || !lastName) {
        throw new Error("Both first name and last name are required");
      }

      if (updatedEmployee.id) {
        setUpdatingEmployeeId(updatedEmployee.id);

        // Find the original employee data
        const originalEmployee = employees.find(
          (emp) => emp.id === updatedEmployee.id,
        );
        if (!originalEmployee) {
          throw new Error("Original employee data not found");
        }

        // Check what has changed
        const hasNameChanged = updatedEmployee.name !== originalEmployee.name;
        const hasTitleChanged =
          updatedEmployee.title !== originalEmployee.title;
        const hasRoleChanged =
          updatedEmployee.roleId !== originalEmployee.roleId;
        const hasEmailChanged =
          (updatedEmployee.email || "").trim() !==
          (originalEmployee as any).email;

        // Only make API calls for changed fields
        if (
          hasNameChanged ||
          hasTitleChanged ||
          hasRoleChanged ||
          hasEmailChanged
        ) {
          await callApi({
            method: "PUT",
            url: `/management/employees/${updatedEmployee.id}`,
            data: {
              // Only include fields that may have changed
              firstName: hasNameChanged ? firstName : undefined,
              lastName: hasNameChanged ? lastName : undefined,
              title: hasTitleChanged ? updatedEmployee.title : undefined,
              roleId: hasRoleChanged ? updatedEmployee.roleId : undefined,
              email: hasEmailChanged
                ? (updatedEmployee.email || "").trim()
                : undefined,
            },
          });
        }

        // Refresh the employee list after successful update
        await fetchEmployees();

        // Track employee update analytics
        analytics.staff("employee_updated", {
          employee_role: updatedEmployee.roleId,
          has_title: !!updatedEmployee.title,
          fields_changed: [
            hasNameChanged && "name",
            hasTitleChanged && "title",
            hasRoleChanged && "role",
            hasEmailChanged && "email",
          ].filter(Boolean),
        });
      } else {
        // Create new employee
        const newEmployee = {
          userId: platformUserId,
          roleId: updatedEmployee.roleId,
          name: {
            firstName,
            lastName,
            title: updatedEmployee.title || "",
          },
        };
        await callApi({
          method: "POST",
          url: "/management/employees",
          data: newEmployee,
        });
        // Refresh the employee list after successful creation
        await fetchEmployees();

        // Track employee creation analytics
        analytics.staff("employee_created", {
          employee_role: updatedEmployee.roleId,
          has_title: !!updatedEmployee.title,
        });
      }
      setEditingEmployee(null);
    } catch (err) {
      console.error("Error saving employee:", err);
      const dup =
        (err as any)?.code === "DUPLICATE" ||
        (err as Error)?.message?.includes("already exists");
      setError(
        dup
          ? "One or more email addresses already exist. Please ensure each employee has a unique email."
          : getFriendlyErrorMessage(err),
      );
    } finally {
      setUpdatingEmployeeId(null);
    }
  };

  const handleRoleChange = (employeeId: string, role: Role) => {
    try {
      if (!employeeId || !role?.roleId) {
        throw new Error("Invalid employee or role data");
      }

      // Find the employee to get their current role and name
      const employee = employees.find((emp) => emp.id === employeeId);
      if (!employee) {
        throw new Error("Employee not found");
      }

      // Don't show confirmation if selecting the same role
      if (employee.roleId === role.roleId) {
        // Close dropdown
        setRoleDropdownOpen((prev) => ({ ...prev, [employeeId]: false }));
        return;
      }

      // Set pending role change and show confirmation modal
      setPendingRoleChange({
        employeeId,
        employeeName: employee.name || "this employee",
        currentRole: employee.role || "No role",
        newRole: role,
      });
      setRoleChangeModalOpen(true);
      // Close dropdown
      setRoleDropdownOpen((prev) => ({ ...prev, [employeeId]: false }));
    } catch (err) {
      console.error("Error preparing role change:", err);
      setError(
        err instanceof Error ? err.message : "Failed to prepare role change",
      );
    }
  };

  const handleRoleChangeConfirm = async () => {
    if (!pendingRoleChange) return;

    try {
      const { employeeId, newRole } = pendingRoleChange;
      setUpdatingEmployeeId(employeeId);
      console.log("Updating role for employee:", {
        employeeId,
        roleId: newRole.roleId,
      });

      // Perform secure role update via authenticated API
      await callApi({
        method: "PUT",
        url: `/management/employees/${employeeId}`,
        data: { roleId: newRole.roleId },
      });

      // Update local state optimistically
      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id === employeeId
            ? {
                ...emp,
                role: newRole.name,
                roleId: newRole.roleId,
                roleColor: getRoleColor(newRole.name),
              }
            : emp,
        ),
      );

      // Close modal and clear pending change
      setRoleChangeModalOpen(false);
      setPendingRoleChange(null);
    } catch (err) {
      console.error("Error during role change:", err);
      setError(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setUpdatingEmployeeId(null);
    }
  };

  const handleRoleChangeCancel = () => {
    setRoleChangeModalOpen(false);
    setPendingRoleChange(null);
  };

  const toggleRoleDropdown = (employeeId: string) => {
    try {
      if (!employeeId) {
        throw new Error("Invalid employee ID");
      }
      setRoleDropdownOpen((prev) => ({
        ...prev,
        [employeeId]: !prev[employeeId],
      }));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to toggle role dropdown",
      );
    }
  };

  const handleSaveNewEmployee = async (employeeData: {
    roleId: string;
    email: string;
    name: {
      firstName: string;
      lastName: string;
      title: string;
    };
  }) => {
    try {
      setIsCreatingEmployee(true);

      await callApi({
        method: "POST",
        url: "/management/employees",
        data: {
          roleId: employeeData.roleId,
          firstName: employeeData.name.firstName,
          lastName: employeeData.name.lastName,
          title: employeeData.name.title,
          email: employeeData.email,
        },
      });

      // Only close modal and refresh on success
      await fetchEmployees();
      setIsAddingEmployee(false);
      setAddEmployeeError(null); // Clear any previous errors
    } catch (err) {
      console.error("Error creating employee:", err);

      // Check the error type to distinguish between duplicate email and validation errors
      const errorType = (err as any)?.response?.data?.error?.type;
      const errorMessage = (err as any)?.response?.data?.error?.message;

      const isValidationError =
        errorType === "ValidationError" ||
        errorMessage?.includes("Invalid data format") ||
        (errorMessage?.includes("Invalid") && errorMessage?.includes("format"));

      const isDuplicate =
        errorMessage?.includes("already exists") ||
        errorMessage?.includes("duplicate") ||
        errorMessage?.includes("unique constraint");

      if (isValidationError) {
        // Show validation error and don't close modal
        setAddEmployeeError(
          "Invalid data format. Please check your input values (e.g., email format).",
        );
      } else if (isDuplicate) {
        // Don't close modal for duplicate errors, show specific error message
        setAddEmployeeError(
          `An employee with the email address "${employeeData.email}" already exists. Please use a different email address.`,
        );
      } else {
        // For other errors, show generic error and don't close modal
        setAddEmployeeError(getFriendlyErrorMessage(err));
      }
      // Modal stays open so user can correct the email
    } finally {
      setIsCreatingEmployee(false);
    }
  };

  // Bulk creation handler with proper duplicate email detection
  const handleSaveBulkEmployees = async (
    employeesData: BulkEmployeeInput[],
  ) => {
    try {
      setIsCreatingEmployee(true);

      const results: {
        email: string;
        success: boolean;
        error?: string;
        errorMessage?: string; // Store full error message
      }[] = [];

      // Process all employees and collect results
      for (const emp of employeesData) {
        try {
          await callApi({
            method: "POST",
            url: "/management/employees",
            data: {
              roleId: emp.roleId,
              firstName: emp.name.firstName,
              lastName: emp.name.lastName,
              title: emp.name.title,
              email: emp.email,
            },
          });

          results.push({
            email: emp.email.toLowerCase(),
            success: true,
          });
        } catch (err) {
          console.error(`Error creating employee ${emp.email}:`, err);

          // Check the error type to categorize different error types
          const errorType = (err as any)?.response?.data?.error?.type;
          const errorMessage = (err as any)?.response?.data?.error?.message;

          const isValidationError =
            errorType === "ValidationError" ||
            errorMessage?.includes("Invalid data format") ||
            (errorMessage?.includes("Invalid") &&
              errorMessage?.includes("format"));

          const isDuplicate =
            errorMessage?.includes("already exists") ||
            errorMessage?.includes("duplicate") ||
            errorMessage?.includes("unique constraint");

          const isConflict =
            errorType === "ConflictError" ||
            errorMessage?.includes("conflict") ||
            errorMessage?.includes("Conflicts with existing data");

          results.push({
            email: emp.email.toLowerCase(),
            success: false,
            error: isDuplicate
              ? "Duplicate email"
              : isValidationError
                ? "Invalid format"
                : isConflict
                  ? "Conflict"
                  : "Failed to create",
            errorMessage: errorMessage || "Unknown error occurred",
          });
        }
      }

      // Check if there were any errors
      const failedResults = results.filter((result) => !result.success);
      const successCount = results.filter((result) => result.success).length;
      const totalCount = results.length;

      // If there are any errors, don't close modal and show inline errors
      if (failedResults.length > 0) {
        // Collect all failed emails for highlighting
        const allFailedEmails = failedResults.map((result) => result.email);
        setDuplicateEmails(allFailedEmails);

        // Build detailed error message
        let errorMsg = `${failedResults.length} of ${totalCount} employees failed to create. `;
        if (successCount > 0) {
          errorMsg += `${successCount} employees were created successfully. `;
        }
        errorMsg += "Please review the highlighted rows below:\n\n";

        // Add specific error for each failed row
        failedResults.forEach((result, idx) => {
          const rowNum = employeesData.findIndex(
            (emp) => emp.email.toLowerCase() === result.email,
          );
          const displayRowNum = rowNum >= 0 ? rowNum + 1 : idx + 1;
          errorMsg += `Row ${displayRowNum} (${result.email}): ${result.errorMessage}\n`;
        });

        setBulkAddError(errorMsg);

        // Persist failed rows so modal can rehydrate inputs after reload
        const failedRows = employeesData.filter((r) =>
          allFailedEmails.includes(r.email.toLowerCase()),
        );
        setBulkAddRetainedRows(failedRows);
        // Refresh employee list for successful creates
        if (successCount > 0) {
          await fetchEmployees();
        }
        return results; // Don't close modal, stay open for corrections
      }

      // If all successful, close modal and refresh
      await fetchEmployees();
      setIsBulkAddingEmployees(false);
      setDuplicateEmails([]);
      setBulkAddRetainedRows(null);

      if (successCount === totalCount) {
        setBulkAddError(null); // Clear any previous errors
      }
      return results;
    } catch (err) {
      console.error("Error in bulk employee creation:", err);
      setBulkAddError(getFriendlyErrorMessage(err));
      return [];
    } finally {
      setIsCreatingEmployee(false);
    }
  };

  const handleDeleteClick = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete?.id) return;

    try {
      setIsDeletingEmployeeId(employeeToDelete.id);

      await callApi({
        method: "DELETE",
        url: `/management/employees/${employeeToDelete.id}`,
      });

      // Remove from local state
      setEmployees((prev) =>
        prev.filter((emp) => emp.id !== employeeToDelete.id),
      );
      setDeleteModalOpen(false);
      setEmployeeToDelete(null);
    } catch (err) {
      console.error("Error deleting employee:", err);
      setError(
        err instanceof Error ? err.message : "Failed to delete employee",
      );
    } finally {
      setIsDeletingEmployeeId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  if (isLoading || isLoadingRoles) {
    return <PageLoadingView />;
  }

  // Show a fatal error screen only if we truly have no data to display.
  if (error && employees.length === 0) {
    return (
      <PageErrorView title="Error loading employees" onRetry={fetchEmployees} />
    );
  }

  if (!roles || roles.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">
          No roles available. Please try again later.
        </p>
      </div>
    );
  }

  const filteredEmployees = employees;

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-full p-6">
        <SelfManageInfoModal
          isOpen={selfManageModalOpen}
          onClose={() => setSelfManageModalOpen(false)}
          onGoToSettings={() => navigate("/settings/employee-profile")}
        />
        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{error}</span>
            <button
              className="absolute top-0 bottom-0 right-0 px-4 py-3 text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100"
              onClick={() => setError(null)}
            >
              <span className="sr-only">Dismiss</span>
              <span className="text-xl">&times;</span>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-2">
            Employee Management
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage your hotel's employees, their roles, and access levels from
            this dashboard.
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto pb-40">
          {/* Title Header */}
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
              Employees
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400 self-center">
              {employees?.length || 0}{" "}
              {employees?.length === 1 ? "employee" : "employees"}
            </span>
          </div>

          {/* Search Bar + Buttons */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <SearchBar
                searchQuery={searchInput}
                onSearchChange={(q) => {
                  setSearchInput(q);
                  if (q === "") {
                    setSearchQuery("");
                  }
                }}
                onSearchSubmit={() => setSearchQuery(searchInput)}
                isLoading={isLoading}
                placeholder="Search for an employee"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleAddEmployee}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-500 flex items-center gap-2 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                <PlusIcon className="w-4 h-4" />
                Add Employee
              </button>
              <button
                onClick={() => setIsBulkAddingEmployees(true)}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-500 flex items-center gap-2 dark:bg-green-500 dark:hover:bg-green-600"
              >
                <PlusIcon className="w-4 h-4" />
                Bulk Add
              </button>
            </div>
          </div>

          {/* Employees Table */}
          {filteredEmployees.length === 0 ? (
            <div className="py-6 text-sm text-gray-500 dark:text-gray-400">
              No employees found.
            </div>
          ) : (
            <Table className="w-full">
              <TableHeader className="border-gray-200 border-b-[3px] dark:border-gray-700">
                <TableRow>
                  <TableCell className="py-2 pl-0 pr-2 font-light text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Name
                  </TableCell>
                  <TableCell className="py-2 px-2 font-light text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                    Title
                  </TableCell>
                  <TableCell className="py-2 px-2 font-light text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Role
                  </TableCell>
                  <TableCell className="py-2 pl-2 pr-0 text-right font-light text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 w-[96px]">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredEmployees.map((employee) => {
                  const isCurrentUser = Boolean(
                    currentUserEmployeeId &&
                    employee?.id === currentUserEmployeeId,
                  );

                  return (
                    <TableRow
                      key={employee?.id}
                      className={
                        isCurrentUser ? "bg-gray-50 dark:bg-gray-800/50" : ""
                      }
                    >
                      {/* Name + email + IDs */}
                      <TableCell className="py-3 pl-0 pr-2 align-top">
                        <div className="flex items-start gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-gray-800 dark:text-white">
                              {employee?.name || "Unknown"}
                              {isCurrentUser && (
                                <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-full">
                                  You
                                </span>
                              )}
                            </div>
                            {employee.email && (
                              <div className="text-sm text-gray-600 dark:text-gray-300 break-all">
                                {employee.email}
                              </div>
                            )}
                            {/* Inline title for small and md screens */}
                            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-300 md:hidden">
                              {employee?.title || "No title"}
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setIdsExpanded((prev) => ({
                                  ...prev,
                                  [employee.id]: !prev[employee.id],
                                }))
                              }
                              className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                              <span>IDs</span>
                              <ChevronDownIcon
                                className={`w-3 h-3 transition-transform ${
                                  idsExpanded[employee.id] ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            {idsExpanded[employee.id] && (
                              <div className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400 break-all">
                                <div>
                                  Employee ID: {employee?.id || "Unknown"}
                                </div>
                                {employee.platformUserId && (
                                  <div>
                                    Platform User ID: {employee.platformUserId}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Title (separate column only on lg+) */}
                      <TableCell className="py-3 px-2 hidden lg:table-cell align-top">
                        <span className="text-gray-600 dark:text-gray-300">
                          {employee?.title || "No title"}
                        </span>
                      </TableCell>

                      {/* Role */}
                      <TableCell className="py-3 px-2 align-top">
                        <div className="relative">
                          <button
                            onClick={() => {
                              if (isCurrentUser) {
                                return;
                              }
                              toggleRoleDropdown(employee.id);
                            }}
                            disabled={
                              updatingEmployeeId === employee.id ||
                              isCurrentUser
                            }
                            aria-disabled={isCurrentUser}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 bg-transparent text-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 text-xs md:text-sm whitespace-nowrap ${
                              updatingEmployeeId === employee.id ||
                              isCurrentUser
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            {updatingEmployeeId === employee.id ? (
                              <div className="flex items-center gap-2">
                                <LoadingSpinner size="sm" color="primary" />
                                <span className="text-gray-800 dark:text-white">
                                  Updating...
                                </span>
                              </div>
                            ) : (
                              <>
                                <span className="max-w-[80px] truncate">
                                  {employee?.role || "No role"}
                                </span>
                                <ChevronDownIcon className="w-4 h-4" />
                              </>
                            )}
                          </button>
                          {roleDropdownOpen[employee.id] && !isCurrentUser && (
                            <Dropdown
                              isOpen={roleDropdownOpen[employee.id]}
                              onClose={() => toggleRoleDropdown(employee.id)}
                              className="min-w-[250px] bg-white dark:bg-gray-800"
                            >
                              {roles?.map((role) => (
                                <DropdownItem
                                  key={role?.roleId}
                                  onClick={() =>
                                    handleRoleChange(employee.id, role)
                                  }
                                  className={`whitespace-nowrap text-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                    updatingEmployeeId === employee.id
                                      ? "opacity-50 cursor-not-allowed pointer-events-none"
                                      : ""
                                  }`}
                                >
                                  {role?.name || "Unknown role"}
                                </DropdownItem>
                              ))}
                            </Dropdown>
                          )}
                        </div>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="py-3 pl-2 pr-0 align-top">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => {
                              if (isCurrentUser) {
                                return;
                              }
                              handleEditEmployee(employee);
                            }}
                            disabled={
                              updatingEmployeeId === employee.id ||
                              isDeletingEmployeeId === employee.id ||
                              isCurrentUser
                            }
                            aria-disabled={isCurrentUser}
                            className={`p-1.5 text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 ${
                              updatingEmployeeId === employee.id ||
                              isDeletingEmployeeId === employee.id ||
                              isCurrentUser
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (isCurrentUser) {
                                return;
                              }
                              handleDeleteClick(employee);
                            }}
                            disabled={
                              updatingEmployeeId === employee.id ||
                              isDeletingEmployeeId === employee.id ||
                              isCurrentUser
                            }
                            aria-disabled={isCurrentUser}
                            className={`p-1.5 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 ${
                              updatingEmployeeId === employee.id ||
                              isDeletingEmployeeId === employee.id ||
                              isCurrentUser
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            {isDeletingEmployeeId === employee.id ? (
                              <LoadingSpinner size="sm" color="primary" />
                            ) : (
                              <TrashIcon className="w-4 h-4" />
                            )}
                          </button>
                          {isCurrentUser && (
                            <button
                              type="button"
                              onClick={() => setSelfManageModalOpen(true)}
                              className="p-1.5 text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400"
                              aria-label="Manage your own details in Settings"
                            >
                              <InformationCircleIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination Controls */}
        {employees && employees.length > 0 && pagination.total > 0 && (
          <div className="py-4 mt-auto flex items-center justify-between sticky bottom-0 z-10 border-t border-gray-200 dark:border-gray-700 px-6 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Show
              </span>
              <select
                value={pagination.limit}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
                className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 dark:text-white pr-8 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                employees per page
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {pagination.page} of{" "}
                {Math.ceil(pagination.total / pagination.limit) || 1}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page >=
                  Math.ceil(pagination.total / pagination.limit)
                }
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Modals */}
        {editingEmployee && (
          <EditEmployeeModal
            isOpen={true}
            onClose={() => setEditingEmployee(null)}
            employee={editingEmployee}
            roles={roles}
            onSave={handleSaveEmployee}
          />
        )}

        {isAddingEmployee && (
          <AddEmployeeModal
            isOpen={true}
            onClose={() => {
              setIsAddingEmployee(false);
              setAddEmployeeError(null); // Clear errors when closing
            }}
            roles={roles}
            onSave={handleSaveNewEmployee}
            isCreating={isCreatingEmployee}
            error={addEmployeeError}
          />
        )}

        {/* Add the delete confirmation modal */}
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          employeeName={employeeToDelete?.name || "this employee"}
          isDeleting={isDeletingEmployeeId === employeeToDelete?.id}
        />

        {/* Add the role change confirmation modal */}
        <RoleChangeConfirmationModal
          isOpen={roleChangeModalOpen}
          onClose={handleRoleChangeCancel}
          onConfirm={handleRoleChangeConfirm}
          employeeName={pendingRoleChange?.employeeName || "this employee"}
          currentRole={pendingRoleChange?.currentRole || "No role"}
          newRole={pendingRoleChange?.newRole?.name || "Unknown"}
          isUpdating={updatingEmployeeId === pendingRoleChange?.employeeId}
        />

        {/* Bulk add employees modal */}
        {isBulkAddingEmployees && (
          <BulkAddEmployeesModal
            isOpen={true}
            onClose={() => {
              setIsBulkAddingEmployees(false);
              setDuplicateEmails([]); // Clear duplicates when closing
              setBulkAddError(null); // Clear errors when closing
              setBulkAddRetainedRows(null);
            }}
            roles={roles}
            onSave={handleSaveBulkEmployees}
            isCreating={isCreatingEmployee}
            duplicateEmails={duplicateEmails}
            error={bulkAddError}
            retainedRows={bulkAddRetainedRows}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default EmployeeManagement;
